<template>
  <div v-if="showDebug" class="token-debug">
    <div class="debug-panel" :class="{ 'minimized': isMinimized }">
      <div class="debug-header">
        <h3>Token Debug</h3>
        <button @click="toggleMinimize" class="minimize-btn">
          <i :class="isMinimized ? 'fas fa-expand' : 'fas fa-compress'"></i>
        </button>
      </div>
      <div v-if="!isMinimized" class="debug-content">
      <div class="debug-info">
        <p><strong>环境:</strong> {{ environment }}</p>
        <p><strong>Token:</strong> {{ tokenStatus }}</p>
        <p><strong>Login Status:</strong> {{ loginStatus }}</p>
        <p><strong>Cookie Token:</strong> {{ cookieToken }}</p>
        <p><strong>LocalStorage Token:</strong> {{ localStorageToken }}</p>
        <p><strong>Katoken:</strong> {{ katoken }}</p>
        <p><strong>认证跳过:</strong> {{ skipAuth ? '是' : '否' }}</p>
        <p v-if="isDevelopment"><strong>角色测试:</strong> {{ devRoleTestEnabled ? '已启用' : '已禁用' }}</p>
        <p><strong>SSO登录状态:</strong> {{ ssoLoginStatus }}</p>
        <div v-if="isDevelopment" class="dev-token-section">
          <p><strong>开发Token:</strong> {{ devToken }}</p>
          <input 
            v-model="newDevToken" 
            placeholder="输入新的开发token"
            class="dev-token-input"
          />
          <button @click="setDevToken" class="debug-btn">设置开发Token</button>
        </div>
        <div v-if="isDevelopment" class="dev-role-test-section">
          <label class="dev-role-test-label">
            <input 
              type="checkbox" 
              v-model="devRoleTestEnabled"
              @change="toggleDevRoleTest"
              class="dev-role-test-checkbox"
            />
            <span>启用角色权限测试（调用真实接口）</span>
          </label>
          <p class="dev-role-test-hint">开启后，角色检查和角色列表将调用真实接口</p>
        </div>
      </div>
      <div class="debug-actions">
        <button @click="refreshStatus" class="debug-btn">刷新状态</button>
        <button @click="clearToken" class="debug-btn">清除Token</button>
        <button @click="checkLogin" class="debug-btn">检查登录</button>
        <button @click="testApiCall" class="debug-btn">测试API调用</button>
        <button @click="validateKatoken" class="debug-btn">验证Katoken</button>
        <button v-if="isDevelopment" @click="generateDevToken" class="debug-btn">生成开发Token</button>
        <button v-if="isDevelopment" @click="testCheckRole" class="debug-btn">测试角色检查</button>
        <button v-if="isDevelopment" @click="testGetRole" class="debug-btn">测试获取角色</button>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
import authService from '@/services/authService'
import creatorService from '@/services/creatorService'

export default {
  name: 'TokenDebug',
  data() {
    return {
      showDebug: process.env.NODE_ENV === 'development',
      tokenStatus: 'Unknown',
      loginStatus: 'Unknown',
      cookieToken: 'Unknown',
      localStorageToken: 'Unknown',
      katoken: 'Unknown',
      devToken: 'Unknown',
      newDevToken: '',
      ssoLoginStatus: 'Unknown',
      environment: process.env.NODE_ENV || 'unknown',
      isDevelopment: process.env.NODE_ENV === 'development',
      isMinimized: false,
      devRoleTestEnabled: false
    }
  },
  computed: {
    skipAuth() {
      // 开发环境：如果启用了角色测试，不跳过认证
      if (this.isDevelopment) {
        return !this.devRoleTestEnabled
      }
      return false
    }
  },
  mounted() {
    // 加载开发环境角色测试开关状态
    this.devRoleTestEnabled = authService.isDevRoleTestEnabled()
    this.refreshStatus()
  },
  methods: {
    /**
     * 刷新状态
     */
    refreshStatus() {
      try {
        // 获取token状态
        const token = authService.getToken()
        this.tokenStatus = token ? '已设置' : '未设置'
        this.localStorageToken = token || '无'

        // 获取cookie中的token
        const cookieToken = this.getCookie('katoken')
        this.cookieToken = cookieToken || '无'

        // 获取katoken（用于API请求）
        this.katoken = token || '无'

        // 获取开发环境token
        if (this.isDevelopment) {
          this.devToken = localStorage.getItem('dev_katoken') || '无'
          // 更新角色测试开关状态
          this.devRoleTestEnabled = authService.isDevRoleTestEnabled()
        }

        // 检查登录状态
        this.checkLoginStatus()

        // 检查SSO登录状态
        this.checkSSOLoginStatus()

      } catch (error) {
        console.error('刷新状态失败:', error)
        this.tokenStatus = '错误'
        this.loginStatus = '错误'
      }
    },

    /**
     * 设置开发Token
     */
    setDevToken() {
      if (this.newDevToken.trim()) {
        authService.setDevToken(this.newDevToken.trim())
        this.newDevToken = ''
        this.refreshStatus()
        console.log('开发Token已设置:', this.newDevToken)
      }
    },

    /**
     * 生成开发Token
     */
    generateDevToken() {
      const newToken = authService.generateDevToken()
      if (newToken) {
        this.refreshStatus()
        console.log('已生成新的开发Token:', newToken)
      }
    },

    /**
     * 清除Token
     */
    clearToken() {
      try {
        authService.clearToken()
        if (this.isDevelopment) {
          localStorage.removeItem('dev_katoken')
        }
        this.refreshStatus()
        console.log('Token已清除')
      } catch (error) {
        console.error('清除Token失败:', error)
      }
    },

    /**
     * 检查登录
     */
    async checkLogin() {
      try {
        const isLoggedIn = await authService.checkLoginStatus()
        this.loginStatus = isLoggedIn ? '已登录' : '未登录'
        console.log('登录状态检查完成:', isLoggedIn)
      } catch (error) {
        console.error('检查登录失败:', error)
        this.loginStatus = '检查失败'
      }
    },

    /**
     * 检查登录状态
     */
    async checkLoginStatus() {
      try {
        const isLoggedIn = await authService.checkLoginStatus()
        this.loginStatus = isLoggedIn ? '已登录' : '未登录'
      } catch (error) {
        console.error('检查登录状态失败:', error)
        this.loginStatus = '检查失败'
      }
    },

    /**
     * 检查SSO登录状态
     */
    async checkSSOLoginStatus() {
      try {
        // 这里可以调用SSO验证接口
        const token = authService.getToken()
        if (token) {
          // 简单的token验证
          this.ssoLoginStatus = '已通过SSO认证'
        } else {
          this.ssoLoginStatus = '未通过SSO认证'
        }
      } catch (error) {
        console.error('检查SSO登录状态失败:', error)
        this.ssoLoginStatus = '检查失败'
      }
    },

    /**
     * 测试API调用
     */
    async testApiCall() {
      try {
        console.log('开始测试API调用...')
        console.log('当前使用的Token:', authService.getToken())
        
        const result = await creatorService.getCreatorLeads({
          pageIndex: 1,
          pageSize: 5
        })
        console.log('API调用成功:', result)
        alert('API调用成功！请查看控制台输出。')
      } catch (error) {
        console.error('API调用失败:', error)
        alert('API调用失败！请查看控制台错误信息。')
      }
    },

    /**
     * 验证Katoken
     */
    async validateKatoken() {
      try {
        const token = authService.getToken()
        if (!token) {
          alert('Katoken不存在！')
          return
        }

        // 验证token格式
        if (token.length < 10) {
          alert('Katoken格式不正确！')
          return
        }

        // 测试API调用验证token有效性
        const result = await creatorService.getCreatorLeads({
          pageIndex: 1,
          pageSize: 1
        })

        if (result && result.data) {
          alert('Katoken验证成功！')
        } else {
          alert('Katoken验证失败！')
        }
      } catch (error) {
        console.error('Katoken验证失败:', error)
        alert('Katoken验证失败！请查看控制台错误信息。')
      }
    },

    /**
     * 获取Cookie
     */
    getCookie(name) {
      const value = `; ${document.cookie}`
      const parts = value.split(`; ${name}=`)
      if (parts.length === 2) return parts.pop().split(';').shift()
      return null
    },
    
    /**
     * 切换最小化状态
     */
    toggleMinimize() {
      this.isMinimized = !this.isMinimized
    },

    /**
     * 切换开发环境角色测试开关
     */
    toggleDevRoleTest() {
      authService.setDevRoleTestEnabled(this.devRoleTestEnabled)
      console.log('开发环境角色测试已', this.devRoleTestEnabled ? '启用' : '禁用')
      // 刷新状态以反映变化
      this.refreshStatus()
      // 提示用户需要刷新页面
      if (this.devRoleTestEnabled) {
        alert('角色测试已启用！建议刷新页面以确保路由守卫生效。')
      }
    },

    /**
     * 测试角色检查接口
     */
    async testCheckRole() {
      try {
        console.log('开始测试角色检查接口...')
        const hasRole = await authService.checkRole()
        console.log('角色检查结果:', hasRole)
        alert(`角色检查结果: ${hasRole ? '有权限' : '无权限'}\n请查看控制台获取详细信息。`)
      } catch (error) {
        console.error('角色检查测试失败:', error)
        alert('角色检查测试失败！请查看控制台错误信息。')
      }
    },

    /**
     * 测试获取角色列表接口
     */
    async testGetRole() {
      try {
        console.log('开始测试获取角色列表接口...')
        const roles = await authService.getRole()
        console.log('角色列表:', roles)
        alert(`获取到 ${roles.length} 个角色:\n${roles.join(', ')}\n请查看控制台获取详细信息。`)
      } catch (error) {
        console.error('获取角色列表测试失败:', error)
        alert('获取角色列表测试失败！请查看控制台错误信息。')
      }
    }
  }
}
</script>

<style scoped>
.token-debug {
  position: fixed;
  bottom: 20px;
  left: 20px;
  z-index: 9999;
}

.debug-panel {
  background: #1a1a1a;
  color: #fff;
  padding: 15px;
  border-radius: 8px;
  font-size: 12px;
  max-width: 350px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.debug-panel.minimized {
  max-width: 200px;
  padding: 10px;
}

.debug-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.debug-header h3 {
  margin: 0;
  color: #00ff00;
  font-size: 14px;
}

.minimize-btn {
  background: none;
  border: none;
  color: #00ff00;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  transition: background 0.2s;
}

.minimize-btn:hover {
  background: #333;
}

.debug-content {
  transition: opacity 0.3s ease;
}



.debug-info p {
  margin: 5px 0;
  line-height: 1.4;
}

.debug-info strong {
  color: #00ff00;
}

.dev-token-section,
.dev-role-test-section {
  margin-top: 10px;
  padding: 10px;
  background: #333;
  border-radius: 4px;
}

.dev-token-input {
  width: 100%;
  padding: 4px 8px;
  margin: 5px 0;
  background: #222;
  color: #fff;
  border: 1px solid #555;
  border-radius: 4px;
  font-size: 11px;
}

.debug-actions {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.debug-btn {
  background: #333;
  color: #fff;
  border: 1px solid #555;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  cursor: pointer;
  transition: background 0.2s;
}

.debug-btn:hover {
  background: #555;
}

.dev-role-test-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #fff;
  font-size: 11px;
}

.dev-role-test-checkbox {
  margin-right: 8px;
  cursor: pointer;
}

.dev-role-test-hint {
  margin: 5px 0 0 0;
  font-size: 10px;
  color: #999;
  font-style: italic;
}
</style> 